import { Box, Divider, Typography } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

import styles from '../../../assets/styles/componentStyles/billingStyles/billingPlanCard.module.scss';

import { useShallow } from 'zustand/react/shallow';

import { PricingPlan } from '../../../models/axiosModel';
import { thousandSeparator } from '../../../utils/constants';
import { CYCLE_TYPES } from '../constant.billing';
import { getPlanDisplayAmount, getPlanDisplayPriceSuffix, getPricingPlanFeatures, BillingId } from '../utils.billing';
import { useBillingStore } from '../../../store/billingStore';

interface PlanCardProps {
    plan: PricingPlan;
    isCurrentPlan?: boolean;
    isSelected?: boolean;
    onSelect?: (planId: BillingId) => void;
}

const PlanCard = ({ plan, isCurrentPlan = false, isSelected = false, onSelect }: PlanCardProps) => {
    
    const { billingCurrencySymbol } = useBillingStore(useShallow((state) => ({
        billingCurrencySymbol: state.billingCurrencySymbol,
    })));

    const planCycle = plan.billing_plan_cycle_period_name;
    const displayAmount = getPlanDisplayAmount(plan.billing_plan_amount, planCycle);
    const priceSuffix = getPlanDisplayPriceSuffix(planCycle);

    // Features always use monthly cycle labels
    const features = getPricingPlanFeatures({
        ...plan,
        billing_plan_cycle_period_name: CYCLE_TYPES.MONTHLY,
    });

    return (
        <Box
            className={`${styles.planCard} ${isSelected ? styles.planCardActive : ''}`}
            onClick={() => onSelect?.(plan.billing_plan_id)}
            sx={{ cursor: onSelect ? 'pointer' : 'default' }}
        >
            <Box className={styles.planCardHeader}>
                <Box className={styles.planTitleRow}>
                    <Typography className={styles.planName}>
                        {plan.billing_plan_name}
                    </Typography>

                    {isCurrentPlan && (
                        <Typography className={styles.currentPlanBadge}>
                            Current Active Plan
                        </Typography>
                    )}
                </Box>

                {plan.billing_plan_description && (
                    <Typography className={styles.planDescription}>
                        {plan.billing_plan_description}
                    </Typography>
                )}
            </Box>

            <Box className={styles.priceRow}>
                <Typography className={styles.planPrice}>
                    {billingCurrencySymbol}{thousandSeparator(displayAmount)}
                </Typography>

                <Typography className={styles.priceSuffix}>
                    {priceSuffix}
                </Typography>
            </Box>

            <Divider className={styles.divider} />

            {features.length > 0 && (
                <Box component="ul" className={styles.featuresList}>
                    {features.map((feature) => (
                        <Box component="li" className={styles.featureItem} key={feature}>
                            <CheckCircleOutlinedIcon className={styles.featureIcon} />

                            <Typography className={styles.featureText}>
                                {feature}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* {isSelected && (
                <Typography className={styles.selectedFooter}>
                    <CheckOutlinedIcon className={styles.selectedFooterIcon} />
                    Selected plan
                </Typography>
            )} */}
        </Box>
    );
};

export default PlanCard;
